import { PSSEData, BusType } from './psseTypes';
import { GridState, GridNode, GridEdge, NodeACFields, EdgeACFields } from '../types';

/**
 * Map the PSSE `busType` to an AC power-flow bus role: the swing bus is the
 * slack, a voltage-controlled generator bus is PV, everything else is PQ.
 */
function acBusKind(busType: BusType): 'slack' | 'pv' | 'pq' {
  if (busType === BusType.SWING) return 'slack';
  if (busType === BusType.GENERATOR) return 'pv';
  return 'pq';
}

export function convertPSSEToGridState(psseData: PSSEData): GridState {
  const nodes: GridNode[] = [];
  const edges: GridEdge[] = [];

  // Everything below is expressed per unit on the system base (MVA). PSSE branch
  // and transformer impedances are already pu; MW/Mvar quantities divide by it.
  const sbase = psseData.header.sbase || 100;

  // Convert buses to nodes
  psseData.buses.forEach(bus => {
    let nodeType: 'generator' | 'load' | 'bus' = 'bus';
    let value = 0;

    // Determine node type based on bus type and connected components
    let minPowerMW: number | undefined;
    let maxPowerMW: number | undefined;

    // Aggregate every component sitting on this bus. A bus can carry a generator
    // *and* a load at once (e.g. IEEE-14 buses 2, 3 and 6), so the AC injection
    // is their net — generation minus load — not one or the other.
    const generators = psseData.generators.filter(gen => gen.busNumber === bus.number);
    const busLoads = psseData.loads.filter(ld => ld.busNumber === bus.number);
    const shunts = psseData.fixedShunts.filter(sh => sh.busNumber === bus.number);

    const genRealMW = generators.reduce((sum, g) => sum + g.realPowerMW, 0);
    const loadRealMW = busLoads.reduce((sum, l) => sum + l.realPowerMW, 0);
    const loadReactiveMVAR = busLoads.reduce((sum, l) => sum + l.reactivePowerMVAR, 0);
    const shuntSusceptanceMVAR = shunts.reduce((sum, s) => sum + s.susceptanceMVAR, 0);

    const hasGenerator = generators.length > 0 &&
      (bus.busType === BusType.SWING || bus.busType === BusType.GENERATOR);

    if (hasGenerator) {
      nodeType = 'generator';

      // For swing/slack bus, don't use the solved power value - it should be calculated
      // For PV buses, use the specified real power
      if (bus.busType === BusType.SWING) {
        // Swing bus power will be calculated to balance the system
        // Start with 0 and let the power flow solver determine the correct value
        value = 0;
      } else {
        // PV bus - use specified real power
        value = genRealMW;
      }

      minPowerMW = generators[0].minRealPowerMW || 0;
      maxPowerMW = generators[0].maxRealPowerMW || 999999; // Ensure reasonable max if not specified
    } else if (busLoads.length > 0) {
      nodeType = 'load';
      value = -loadRealMW; // Negative for loads
    }

    // AC power-flow overrides — the *specified* net injection and bus role, so
    // ACPF solves the real case instead of falling back to nameplate capacity.
    const kind = acBusKind(bus.busType);
    const netRealPU = (genRealMW - loadRealMW) / sbase;
    const ac: NodeACFields = { busKind: kind };
    if (kind === 'slack' || kind === 'pv') {
      // Voltage-controlled: hold the generator's scheduled voltage. The slack's
      // real injection is free (it balances the system), so its pSet is nominal
      // only; a PV bus holds its net real injection.
      ac.vSet = generators[0]?.voltageMagnitude ?? bus.voltageMagnitude;
      ac.pSet = netRealPU;
    } else {
      // PQ: both real and reactive injections are specified (load is negative).
      ac.pSet = netRealPU;
      ac.qSet = -loadReactiveMVAR / sbase;
    }
    // A fixed shunt (e.g. a capacitor bank) contributes B·V² to reactive balance.
    if (shuntSusceptanceMVAR !== 0) ac.shuntB = shuntSusceptanceMVAR / sbase;

    nodes.push({
      id: bus.number.toString(),
      type: nodeType,
      x: bus.number * 50, // Simple positioning based on bus number
      y: 100 + (bus.number % 5) * 80,
      value: value,
      voltage: bus.voltageMagnitude,
      minPowerMW,
      maxPowerMW,
      // Add default cost data for generators to ensure they participate in dispatch
      costPerMWh: nodeType === 'generator' ? 50 + bus.number : undefined,
      ac,
    });
  });

  // Convert branches to edges
  psseData.branches.forEach((branch, index) => {
    // Use rateA if available, otherwise calculate a reasonable rating based on impedance
    let rating = branch.rateA;
    if (rating === 0) {
      // Estimate rating based on line impedance - lower impedance = higher capacity
      const impedance = Math.sqrt(branch.resistance ** 2 + branch.reactance ** 2);
      rating = impedance > 0 ? Math.max(100, 1000 / (impedance * 10)) : 500;
    }

    const ac: EdgeACFields = {
      r: branch.resistance,
      x: branch.reactance,
      b: branch.chargingSusceptance,
      tap: branch.tapRatio || 1,
    };

    edges.push({
      id: `branch_${branch.fromBus}_${branch.toBus}_${index}`,
      from: branch.fromBus.toString(),
      to: branch.toBus.toString(),
      resistance: branch.resistance, // series resistance (pu)
      reactance: branch.reactance, // series reactance (pu) — used by DC power flow
      rating: Math.round(rating),
      ac,
    });
  });

  // Convert transformers to edges
  psseData.transformers.forEach((transformer, index) => {
    // Include transformers even with zero resistance/reactance - they provide connectivity
    const reactance = transformer.reactance || 0.01; // Use small default if zero

    const ac: EdgeACFields = {
      r: transformer.resistance,
      x: reactance,
      // The off-nominal turns ratio sits on the `from` (winding 1) side.
      tap: transformer.tapRatio || 1,
    };

    edges.push({
      id: `transformer_${transformer.fromBus}_${transformer.toBus}_${index}`,
      from: transformer.fromBus.toString(),
      to: transformer.toBus.toString(),
      resistance: transformer.resistance, // series resistance (pu)
      reactance: reactance, // series reactance (pu) — used by DC power flow
      rating: 1000, // Default rating for transformers
      ac,
    });
  });

  return {
    nodes,
    edges
  };
}
